import type { NavItem, NavSection, NavWorkflowGroup } from '@/components/layout/Sidebar';
import { masterAdminNav, orgAdminNav, posNav } from '@/config/navigation';
import { SUBSCRIPTION_VALIDATION_ENABLED } from '@/config/subscriptionPolicy';
import { getAppNavConfig } from '@/config/appNavigation';
import { getModuleWorkNavItems, WORK_NAV_GROUP } from '@/config/globalWorkspaceNav';
import { filterNavItemsByEntryModule } from '@/config/moduleEntries';
import {
  dedupeNavItems,
  resolveModuleAdminRouteFeature,
  resolveModuleAdminRoutePermission,
} from '@/config/moduleDefaults';
import { isOrgOwner } from '@/utils/moduleAccess';
import { ORG_ROUTE_ACCESS, ORG_ROUTE_FEATURES, ORG_ROUTE_VIEW_PERMISSIONS } from '@/config/routeAccessRegistry';
import { checkPermissionRule } from '@/config/routeAccessUtils';
import type { ErpModule } from '@/api/platform.api';
import type { UiScreenActions } from '@/types/ui';
import { isGuardedModulePath } from '@/utils/routeGuardUtils';

/** @deprecated Legacy flat sections — use getAppWorkflowNav */
export { orgNavSections } from '@/config/orgNavSections';

const MANAGE_PERMISSIONS = [
  'ORG_MANAGE',
  'STORE_MANAGE',
  'EMPLOYEE_MANAGE',
  'PRODUCT_MANAGE',
  'INVENTORY_MANAGE',
  'ROLE_MANAGE',
  'SUBSCRIPTION_MANAGE',
];

export function isPosOnlyUser(permissions: string[]): boolean {
  if (!permissions.includes('BILLING_CREATE')) return false;
  return !MANAGE_PERMISSIONS.some((p) => permissions.includes(p));
}

export function filterNavByPermissions(
  items: NavItem[],
  has: (code: string) => boolean,
  permissions: string[] = [],
  roleCode?: string | null,
): NavItem[] {
  const owner = isOrgOwner(permissions, roleCode);
  return items.filter((item) => {
    const path = item.to.split('?')[0];
    const rule = ORG_ROUTE_ACCESS[path] ?? ORG_ROUTE_ACCESS[item.to];
    if (!rule) {
      const fallbackPermission = resolveOrgRoutePermission(path);
      if (fallbackPermission !== undefined) {
        if (owner) return true;
        return checkPermissionRule(fallbackPermission, has);
      }
      return !isGuardedModulePath(path);
    }
    if (owner) return true;
    return checkPermissionRule(rule.view, has);
  });
}

export function filterNavByFeatures(items: NavItem[], hasFeature: (code: string) => boolean): NavItem[] {
  if (!SUBSCRIPTION_VALIDATION_ENABLED) {
    return items;
  }
  return items.filter((item) => {
    if (!item.feature) return true;
    return hasFeature(item.feature);
  });
}

function screenActionsReady(screenActions: Record<string, UiScreenActions> | null | undefined): boolean {
  return screenActions != null && Object.keys(screenActions).length > 0;
}

/** True when the user may open this route (navigation visibility). */
export function canViewNavPath(
  path: string,
  screenActions: Record<string, UiScreenActions> | null | undefined,
  has: (code: string) => boolean,
  permissions: string[] = [],
  roleCode?: string | null,
): boolean {
  const basePath = path.split('?')[0];

  if (screenActionsReady(screenActions)) {
    const actions = screenActions![basePath];
    if (actions) {
      return actions.canView;
    }
    if (isOrgOwner(permissions, roleCode)) return true;
    // Backend screenActions manifest is authoritative — omit means not visible.
    return false;
  }

  const rule = ORG_ROUTE_ACCESS[basePath] ?? ORG_ROUTE_ACCESS[path];
  if (!rule) {
    const fallbackPermission = resolveOrgRoutePermission(basePath);
    if (fallbackPermission !== undefined) {
      if (isOrgOwner(permissions, roleCode)) return true;
      return checkPermissionRule(fallbackPermission, has);
    }
    return !isGuardedModulePath(basePath);
  }
  if (isOrgOwner(permissions, roleCode)) return true;
  return checkPermissionRule(rule.view, has);
}

export function filterNavByScreenActions(
  items: NavItem[],
  screenActions: Record<string, UiScreenActions> | null | undefined,
  has: (code: string) => boolean,
  permissions: string[] = [],
  roleCode?: string | null,
): NavItem[] {
  return items.filter((item) =>
    canViewNavPath(item.to, screenActions, has, permissions, roleCode),
  );
}

/** Apply view + subscription filters to sidebar groups. */
export function filterNavGroupsByAccess(
  groups: NavWorkflowGroup[],
  screenActions: Record<string, UiScreenActions> | null | undefined,
  has: (code: string) => boolean,
  permissions: string[] = [],
  hasFeature: (code: string) => boolean = () => true,
  roleCode?: string | null,
): NavWorkflowGroup[] {
  return groups
    .map((group) => {
      let items = dedupeNavItems(group.items);
      items = filterNavByScreenActions(items, screenActions, has, permissions, roleCode);
      items = filterNavByFeatures(items, hasFeature);
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);
}

/** Default-enabled modules when API has no row (aligned with backend ErpModuleDefaults — none). */
const DEFAULT_ENABLED_MODULES: ErpModule[] = [];

const WORK_NAV_GROUP_IDS = new Set(['work', 'workflows']);

/** Legacy backend menus may still emit a standalone recruitment group. */
function normalizeHrNavGroups(
  groups: NavWorkflowGroup[],
  module?: ErpModule,
): NavWorkflowGroup[] {
  if (module !== 'HR') return groups;
  const recruitment = groups.find((group) => group.id === 'recruitment');
  if (!recruitment || recruitment.items.length === 0) return groups;

  const rest = groups.filter((group) => group.id !== 'recruitment');
  const talentIndex = rest.findIndex((group) => group.id === 'talent');
  const mergedItems = dedupeNavItems([
    ...recruitment.items,
    ...(talentIndex >= 0 ? rest[talentIndex].items : []),
  ]);

  if (talentIndex < 0) {
    return [
      ...rest,
      {
        id: 'talent',
        label: 'Talent & performance',
        items: mergedItems,
      },
    ];
  }

  const next = [...rest];
  next[talentIndex] = {
    ...rest[talentIndex],
    label: rest[talentIndex].label || 'Talent & performance',
    items: mergedItems,
  };
  return next;
}

/** Collapse duplicate Work / Workflows sidebar sections into one group. */
function mergeWorkNavGroups(groups: NavWorkflowGroup[]): NavWorkflowGroup[] {
  const workItems: NavItem[] = [];
  const rest: NavWorkflowGroup[] = [];

  for (const group of groups) {
    if (WORK_NAV_GROUP_IDS.has(group.id)) {
      workItems.push(...group.items);
    } else {
      rest.push(group);
    }
  }

  if (workItems.length === 0) return groups;

  const workGroup: NavWorkflowGroup = {
    ...WORK_NAV_GROUP,
    items: dedupeNavItems(workItems),
  };

  const dashboard = rest.find((g) => g.id === 'dashboard' && g.items.length > 0);
  const withoutDashboard = rest.filter((g) => g.id !== 'dashboard');
  return [...(dashboard ? [dashboard] : []), workGroup, ...withoutDashboard];
}

/** Module-scoped Work section — approvals inbox, notifications, workflow builder, etc. */
export function getGlobalWorkNavGroups(
  activeModule: ErpModule,
  has: (code: string) => boolean,
  hasFeature: (code: string) => boolean = () => true,
  permissions: string[] = [],
  screenActions?: Record<string, UiScreenActions> | null,
  roleCode?: string | null,
): NavWorkflowGroup[] {
  let items = dedupeNavItems(getModuleWorkNavItems(activeModule));
  items = filterNavByScreenActions(items, screenActions, has, permissions, roleCode);
  items = filterNavByPermissions(items, has, permissions, roleCode);
  items = filterNavByFeatures(items, hasFeature);
  if (items.length === 0) return [];
  return [{ ...WORK_NAV_GROUP, items }];
}

function groupSortRank(_module: ErpModule | undefined, groupId: string, appOrder: string[]): number {
  if (groupId === 'administration') return 9_000;
  if (groupId === 'work') return 1;
  const idx = appOrder.indexOf(groupId);
  if (idx >= 0) return idx;
  return 5_000;
}

/** Apply canonical labels, item order, and group order from appNavigation.ts */
export function applyAppNavManifest(
  module: ErpModule,
  groups: NavWorkflowGroup[],
): NavWorkflowGroup[] {
  const config = getAppNavConfig(module);
  if (!config) {
    return [...groups].sort(
      (a, b) => groupSortRank(module, a.id, []) - groupSortRank(module, b.id, []),
    );
  }

  const groupMeta = new Map(config.groups.map((g) => [g.id, g]));
  const labelByPath = new Map<string, string>();
  const itemOrderByGroup = new Map<string, string[]>();
  const appOrder = config.groups.map((g) => g.id);

  for (const group of config.groups) {
    itemOrderByGroup.set(
      group.id,
      group.items.map((item) => item.to.split('?')[0]),
    );
    for (const item of group.items) {
      labelByPath.set(item.to.split('?')[0], item.label);
    }
  }

  const enriched = groups.map((group) => {
    const meta = groupMeta.get(group.id);
    const pathOrder = itemOrderByGroup.get(group.id) ?? [];
    const items = [...group.items]
      .sort((a, b) => {
        const ai = pathOrder.indexOf(a.to.split('?')[0]);
        const bi = pathOrder.indexOf(b.to.split('?')[0]);
        return (ai >= 0 ? ai : 999) - (bi >= 0 ? bi : 999);
      })
      .map((item) => ({
        ...item,
        label: labelByPath.get(item.to.split('?')[0]) ?? item.label,
      }));
    return {
      ...group,
      label: meta?.label ?? group.label,
      pinned: meta?.pinned ?? group.pinned,
      items,
    };
  });

  return [...enriched].sort(
    (a, b) => groupSortRank(module, a.id, appOrder) - groupSortRank(module, b.id, appOrder),
  );
}

/** Sidebar order: module dashboard first, then unified Work, then remaining module groups. */
export function mergeBackendWithCanonicalNav(
  backendGroups: NavWorkflowGroup[],
  module: ErpModule,
  has: (code: string) => boolean,
  permissions: string[] = [],
  hasFeature: (code: string) => boolean = () => true,
  roleCode?: string | null,
  screenActions?: Record<string, UiScreenActions> | null,
): NavWorkflowGroup[] {
  const canonical = getAppWorkflowNav(module, has, permissions, hasFeature, roleCode, screenActions);
  if (canonical.length === 0) {
    return filterNavGroupsByAccess(backendGroups, screenActions, has, permissions, hasFeature, roleCode);
  }

  const backendById = new Map(backendGroups.map((group) => [group.id, group]));
  const merged: NavWorkflowGroup[] = [];

  for (const canonGroup of canonical) {
    const backendGroup = backendById.get(canonGroup.id);
    const itemsByPath = new Map<string, NavItem>();

    for (const item of backendGroup?.items ?? []) {
      itemsByPath.set(item.to.split('?')[0], item);
    }
    // Backend menu is authoritative for visibility — canonical config only enriches existing items.
    for (const item of canonGroup.items) {
      const path = item.to.split('?')[0];
      const existing = itemsByPath.get(path);
      if (existing) {
        itemsByPath.set(path, { ...existing, label: item.label ?? existing.label });
      }
    }

    const items = dedupeNavItems([...itemsByPath.values()]);
    if (items.length === 0) continue;

    merged.push({
      ...canonGroup,
      label: canonGroup.label ?? backendGroup?.label ?? canonGroup.id,
      pinned: canonGroup.pinned ?? backendGroup?.pinned,
      items,
    });
    backendById.delete(canonGroup.id);
  }

  for (const group of backendById.values()) {
    if (group.items.length > 0) merged.push(group);
  }

  return filterNavGroupsByAccess(
    normalizeHrNavGroups(merged, module),
    screenActions,
    has,
    permissions,
    hasFeature,
    roleCode,
  );
}

/** Sidebar order: module dashboard first, then unified Work, then remaining module groups. */
export function composeWorkspaceNavGroups(
  moduleGroups: NavWorkflowGroup[],
  globalGroups: NavWorkflowGroup[] = [],
  module?: ErpModule,
): NavWorkflowGroup[] {
  const normalized = normalizeHrNavGroups(moduleGroups, module);
  const manifestGroups = module ? applyAppNavManifest(module, normalized) : normalized;
  const dashboard = manifestGroups.find((group) => group.id === 'dashboard' && group.items.length > 0);
  const restModule = manifestGroups.filter((group) => group.id !== 'dashboard' && group.id !== 'work');
  return mergeWorkNavGroups([
    ...(dashboard ? [dashboard] : []),
    ...globalGroups,
    ...restModule,
  ]);
}

export function getAppWorkflowNav(
  activeModule: ErpModule,
  has: (code: string) => boolean,
  permissions: string[] = [],
  hasFeature: (code: string) => boolean = () => true,
  roleCode?: string | null,
  screenActions?: Record<string, UiScreenActions> | null,
): NavWorkflowGroup[] {
  const config = getAppNavConfig(activeModule);
  if (!config) return [];

  const owner = isOrgOwner(permissions, roleCode);

  return config.groups
    .filter((group) => {
      if (group.id === 'administration') return owner;
      return true;
    })
    .map((group) => {
      let items = dedupeNavItems(group.items);
      items = filterNavItemsByEntryModule(items, activeModule);
      items = filterNavByScreenActions(items, screenActions, has, permissions, roleCode);
      items = filterNavByPermissions(items, has, permissions, roleCode);
      items = filterNavByFeatures(items, hasFeature);
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);
}

/** @deprecated Use getAppWorkflowNav — returns single-app workflow groups only */
export function getOrgNavSections(
  has: (code: string) => boolean,
  permissions: string[] = [],
  _isModuleEnabled: (module: ErpModule) => boolean,
  entryModule: ErpModule | null = null,
): NavSection[] {
  if (!entryModule) return [];
  const groups = getAppWorkflowNav(entryModule, has, permissions, () => true, undefined);
  return groups.map((g) => ({ id: g.id, label: g.label, module: entryModule, items: g.items }));
}

/** @deprecated Flat list — prefer getOrgNavSections */
export function getOrgNav(
  has: (code: string) => boolean,
  permissions: string[] = [],
  hasFeature: (code: string) => boolean = () => true,
): NavItem[] {
  if (isPosOnlyUser(permissions)) {
    return [];
  }
  const byPerm = filterNavByPermissions(orgAdminNav, has);
  return filterNavByFeatures(byPerm, hasFeature);
}

export function createEntryModuleChecker(entryModule: ErpModule): (module: ErpModule) => boolean {
  return (module: ErpModule) => module === entryModule;
}

export function createModuleEnabledChecker(
  modules: { module: ErpModule; enabled: boolean }[],
  authModules: string[] = [],
): (module: ErpModule) => boolean {
  return (module: ErpModule) => {
    const row = modules.find((m) => m.module === module);
    if (row) return row.enabled;
    if (authModules.length > 0) return authModules.includes(module);
    return DEFAULT_ENABLED_MODULES.includes(module);
  };
}

export function getPosNav(
  has: (code: string) => boolean,
  hasFeature: (code: string) => boolean = () => true,
): NavItem[] {
  const byPerm = filterNavByPermissions(posNav, has);
  return filterNavByFeatures(byPerm, hasFeature);
}

export function getMasterNav(): NavItem[] {
  return masterAdminNav;
}

export function resolveRouteFeature(pathname: string): string | null {
  const adminFeature = resolveModuleAdminRouteFeature(pathname);
  if (adminFeature !== undefined) {
    return adminFeature;
  }
  if (ORG_ROUTE_FEATURES[pathname] !== undefined) {
    const rule = ORG_ROUTE_FEATURES[pathname];
    return typeof rule === 'string' ? rule : null;
  }
  const prefix = Object.keys(ORG_ROUTE_FEATURES)
    .filter((path) => path !== '/pos' && pathname.startsWith(path + '/'))
    .sort((a, b) => b.length - a.length)[0];
  if (!prefix) {
    return null;
  }
  const rule = ORG_ROUTE_FEATURES[prefix];
  return typeof rule === 'string' ? rule : null;
}

export function resolveOrgRoutePermission(pathname: string): string | string[] | null | undefined {
  const adminPerm = resolveModuleAdminRoutePermission(pathname);
  if (adminPerm !== undefined) {
    return adminPerm;
  }
  if (ORG_ROUTE_VIEW_PERMISSIONS[pathname] !== undefined) {
    return ORG_ROUTE_VIEW_PERMISSIONS[pathname];
  }
  const prefix = Object.keys(ORG_ROUTE_VIEW_PERMISSIONS)
    .filter((path) => path !== '/pos' && pathname.startsWith(path + '/'))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? ORG_ROUTE_VIEW_PERMISSIONS[prefix] : undefined;
}

